/**
 * DayOfWeekChart — email volume by day of week (received + sent).
 */

import React from 'react';
import { Typography, Empty } from 'antd';
import { Bar } from 'react-chartjs-2';
import { CalendarDays } from 'lucide-react';

const { Text } = Typography;

export function DayOfWeekChart({ metrics, isDark }) {
  const { dayOfWeekData } = metrics;
  const tickColor = isDark ? '#6b7280' : '#9ca3af';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  const hasData = dayOfWeekData.some((d) => d.total > 0);

  const data = {
    labels: dayOfWeekData.map((d) => d.day),
    datasets: [
      {
        label: 'Received',
        data: dayOfWeekData.map((d) => d.received),
        backgroundColor: isDark ? 'rgba(99,102,241,0.7)' : 'rgba(99,102,241,0.65)',
        hoverBackgroundColor: 'rgba(99,102,241,0.9)',
        borderRadius: 4,
        barPercentage: 0.7,
      },
      {
        label: 'Sent',
        data: dayOfWeekData.map((d) => d.sent),
        backgroundColor: isDark ? 'rgba(16,185,129,0.7)' : 'rgba(16,185,129,0.65)',
        hoverBackgroundColor: 'rgba(16,185,129,0.9)',
        borderRadius: 4,
        barPercentage: 0.7,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: tickColor,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: { size: 11, weight: 500 },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: isDark ? '#1f2937' : '#fff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#6b7280',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          footer: (items) => {
            const total = items.reduce((s, i) => s + i.parsed.y, 0);
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: tickColor, font: { size: 11 } },
        border: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { precision: 0, color: tickColor, font: { size: 11 } },
        grid: { color: gridColor, drawBorder: false },
        border: { display: false },
      },
    },
  };

  return (
    <div style={{
      borderRadius: 14,
      background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
      boxShadow: isDark
        ? '0 1px 3px rgba(0,0,0,0.2)'
        : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
      padding: '20px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CalendarDays size={16} color="#8b5cf6" strokeWidth={2} />
        </div>
        <Text strong style={{ fontSize: 14 }}>Day-of-Week Patterns</Text>
      </div>
      {!hasData ? (
        <Empty description="No data" />
      ) : (
        <div style={{ height: 240, flex: 1 }}>
          <Bar data={data} options={options} />
        </div>
      )}
    </div>
  );
}
